/**
 * cart.js — centralized cart-add logic for Just Thrive landing pages
 *
 * Phase 1 (this file): URL-navigation patterns only — Pattern A (one-time)
 * and Pattern B (subscription) from JT_Loop_Cart_Links_Reference.md.
 * No /cart/add.js, no /cart/clear.js — pure URL building + navigation.
 *
 * Why URL navigation, not JS cart API:
 *   1. Cross-origin safety — LPs run on get.justthrivehealth.com but Shopify cart
 *      API is on justthrivehealth.com. URL navigation sidesteps CORS entirely.
 *   2. Attribution preservation — gotcha #3 in the reference doc warns that
 *      /cart/clear.js wipes Triple Whale, Northbeam, and Social Snowball
 *      attribution. URL navigation never touches the cart API, never clears.
 *   3. Simplicity — one redirect per click, no async race between clear/add/update.
 *
 * Phase 2 (when BX GWP ships, multi-item carts with welcome gifts):
 *   Add Pattern C with capture → clear → add → restore → discount → checkout.
 *   For now Pattern A and B cover every active LP.
 *
 * Page configuration:
 *   Each LP inlines a window.JT_CART_CONFIG snapshot at build time:
 *     window.JT_CART_CONFIG = {
 *       sku: 'JTPRO90',
 *       product_name: 'Just Thrive Probiotic 90-day',
 *       variant_id: '40805432262721',                 // build-time snapshot
 *       selling_plans: { '90day': '844103745', ... }, // build-time snapshot
 *       default_tier: '90day'
 *     };
 *
 *   At page load, cart.js fetches /lp-config/cart_ids.json (60s cache via
 *   sessionStorage) and overrides the inlined snapshot with current values.
 *   If the runtime fetch fails, the inlined snapshot keeps the page working.
 *   This means Peter can rotate Loop plan IDs by editing one JSON and pushing
 *   — no per-LP regeneration required.
 *
 * Public API exposed on window:
 *   window.JT.cart.checkoutNow(sku, tier)  — direct checkout
 *   window.JT.cart.tierPicked(sku)         — reads selected radio[name=jt-tier]
 */

(function (window, document) {
  'use strict';

  var SHOP = 'https://justthrivehealth.com';
  var CONFIG_URL = '/lp-config/cart_ids.json';
  var CONFIG_CACHE_KEY = 'jt_lp_cart_config_v1';
  var CONFIG_CACHE_TTL_MS = 60 * 1000; // 60s — matches Vercel cache header

  // Attribution params we forward from the LP URL to checkout.
  // Shopify preserves URL params on cart permalinks, so these survive.
  // KEEP IN SYNC WITH lib/attribution.mjs — the serverless mint route
  // (api/r/[campaign].js) imports the same key list from there because this IIFE
  // has no exports. If you edit this list, edit lib/attribution.mjs too.
  var ATTRIBUTION_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'msclkid', 'twclid', 'dclid', 'ttclid', 'ko_click_id',
    'sscid', 'sscid_force_redirect', 'tw_source',
    'jt_lp_slug', 'jt_lp_cluster', 'jt_lp_theme',
    // DHP advertorial (2026-06-05): forward the email's source + send tags to
    // checkout as cart attributes so the 3-send test is attributable end to end.
    // Additive only — keys are forwarded just when present in the landing URL.
    'source', 'send',
    // Partnership umbrella key (2026-07): the mint route forces campaign_key onto
    // the landing URL as an invariant; forwarding it as a cart attribute carries
    // it onto the order so orders stay attributable to the partnership even when
    // UTMs are lost on a later session.
    'campaign_key'
  ];

  function fetchJsonNoStore(url) {
    return fetch(url, { cache: 'no-cache', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('config fetch ' + r.status);
        return r.json();
      });
  }

  function getRuntimeConfig() {
    try {
      var cached = sessionStorage.getItem(CONFIG_CACHE_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.ts && (Date.now() - parsed.ts) < CONFIG_CACHE_TTL_MS) {
          return Promise.resolve(parsed.data);
        }
      }
    } catch (e) { /* sessionStorage may be disabled — continue */ }

    return fetchJsonNoStore(CONFIG_URL).then(function (data) {
      try {
        sessionStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
      } catch (e) { /* fail silently */ }
      return data;
    }).catch(function (err) {
      console.warn('[JT cart] runtime config fetch failed, using inlined fallback:', err.message);
      return null;
    });
  }

  function resolveIds(runtime, sku, tier) {
    var inline = window.JT_CART_CONFIG;
    if (!inline) {
      throw new Error('JT_CART_CONFIG missing — page is misconfigured (no inline cart config)');
    }
    var skuKey = sku || inline.sku;
    if (!skuKey) {
      throw new Error('No SKU specified at call site or in JT_CART_CONFIG');
    }
    // Prefer runtime config; fall back to inline snapshot.
    var skuData = (runtime && runtime.skus && runtime.skus[skuKey]) || null;
    if (!skuData && inline.skus && inline.skus[skuKey]) skuData = inline.skus[skuKey];
    if (!skuData && inline.sku === skuKey) skuData = inline; // single-SKU inline form
    if (!skuData) {
      throw new Error('No data for SKU ' + skuKey + ' in runtime config or inline snapshot');
    }
    var variantId = skuData.variant_id;
    if (!variantId) throw new Error('No variant_id for SKU ' + skuKey);
    var planId = null;
    if (tier && tier !== 'onetime') {
      var plans = skuData.selling_plans || {};
      planId = plans[tier] || null;
      if (!planId || planId === 'TBD' || planId === 'n/a') {
        throw new Error('No valid selling_plan for SKU ' + skuKey + ' tier ' + tier + ' (got: ' + planId + ')');
      }
    }
    return { variantId: variantId, planId: planId, sku: skuKey };
  }

  function attributionParamsFromUrl() {
    var incoming;
    try {
      incoming = new URLSearchParams(window.location.search);
    } catch (e) {
      return [];
    }
    var forwarded = [];
    for (var i = 0; i < ATTRIBUTION_KEYS.length; i++) {
      var key = ATTRIBUTION_KEYS[i];
      var value = incoming.get(key);
      if (value) forwarded.push([key, value]);
    }
    return forwarded;
  }

  var COHORT_COOKIE = 'jt_cohort_first_touch';
  var COHORT_COOKIE_MAX_AGE_DAYS = 90;

  function readCohortCookie() {
    if (typeof document === 'undefined' || !document.cookie) return null;
    var match = document.cookie.match(new RegExp('(?:^|; )' + COHORT_COOKIE + '=([^;]*)'));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (e) {
      return null;
    }
  }

  function writeCohortCookie(value) {
    if (typeof document === 'undefined') return;
    var expires = new Date();
    expires.setTime(expires.getTime() + COHORT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
    document.cookie = COHORT_COOKIE + '=' + encodeURIComponent(JSON.stringify(value)) +
      '; expires=' + expires.toUTCString() +
      '; path=/' +
      '; SameSite=Lax' +
      '; Secure';
  }

  function readLpCohort() {
    // Returns { experiment_id, lp_variant, lp_slug, lp_cluster } using first-touch semantics.
    //
    // The body data-* attrs reflect the page the buyer is currently on.
    // For cohort analysis we want the page they FIRST encountered, even
    // if they later browse to a different LP and ATC there. Otherwise a
    // buyer who lands on -a, browses to -c, and ATCs on -c gets
    // attributed to -c, masking the originating LP's actual conversion
    // contribution.
    //
    // Persistence: a first-party cookie on get.justthrivehealth.com.
    // 90-day expiry, SameSite=Lax (so it survives the round-trip to
    // /checkout and back), Secure (HTTPS only). Cookies survive
    // incognito and Safari ITP private-browsing more reliably than
    // localStorage, which can silently report quota-zero and let writes
    // appear to succeed while reads return null.
    //
    // Degradation: if document.cookie is unavailable (sandboxed iframe,
    // exotic environment) we fall back to last-touch from the body
    // attrs. Better than no cohort.

    var body = document.body;
    var ctx = (typeof window !== 'undefined' && window.JT_LP_CONTEXT) || {};
    var current = {
      experiment_id: (body && body.dataset && body.dataset.experimentId) || null,
      lp_variant:    (body && body.dataset && body.dataset.lpVariant)    || null,
      lp_slug:       ctx.lp_slug   || null,
      lp_cluster:    ctx.lp_cluster || null
    };

    var stored = readCohortCookie();
    if (stored && stored.experiment_id) {
      return {
        experiment_id: stored.experiment_id,
        lp_variant:    stored.lp_variant  || null,
        lp_slug:       stored.lp_slug     || null,
        lp_cluster:    stored.lp_cluster  || null
      };
    }

    // No stored first-touch yet. If we have a current experiment_id,
    // capture it and return. If we don't (utility page), return nulls
    // and skip the append.
    if (current.experiment_id) {
      writeCohortCookie({
        experiment_id: current.experiment_id,
        lp_variant:    current.lp_variant,
        lp_slug:       current.lp_slug,
        lp_cluster:    current.lp_cluster,
        first_seen_at: new Date().toISOString()
      });
    }
    return current;
  }

  // Append an optional plain object of extra cart attributes as
  // attributes[key]=value parts (same shape as the attribution/cohort attrs).
  // Used to tag e.g. dhp_upsell=<path> on the order. No-op when attrs is falsy.
  function appendExtraAttrs(addParts, attrs) {
    if (!attrs) return;
    for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
      if (attrs[k] == null || attrs[k] === '') continue;
      addParts.push('attributes[' + encodeURIComponent(k) + ']=' + encodeURIComponent(attrs[k]));
    }
  }

  function buildCheckoutUrl(variantId, planId, attrs, discount) {
    // Chained URL pattern modeled after MannaVitality:
    //
    //   /cart/clear → /cart/add → /checkout
    //
    // via Shopify's native return_to chain. Lands user directly on /checkout (no
    // intermediate /cart page) without using the rotted &checkout=true flag.
    //
    // Encoding: the inner /cart/add URL is the value of the outer return_to= param,
    // so its internal `&` separators are encoded as `%26` to prevent the outer URL
    // parser from splitting them. After Shopify URL-decodes return_to, the inner
    // URL has literal `&`, which the next request parses correctly.
    //
    // Attribution preservation: /cart/clear wipes cart attributes (gotcha #3). We
    // immediately re-set them via /cart/add?attributes[key]=value in the same chain,
    // so attribution params (utm_*, fbclid, msclkid, etc.) survive to /checkout.
    // VERIFIED working on JT 2026-05-04: cart.attributes correctly populated.
    //
    // Single-item form (`?id=X&quantity=1&selling_plan=Y`):
    // We use the LEGACY SINGLE-ITEM form, NOT the unindexed array form
    // (`items[][id]=...&items[][selling_plan]=...`). Reason: the array form is
    // ambiguous — Shopify's parser doesn't reliably bind `items[][selling_plan]`
    // to the same item as `items[][id]`, so the subscription drops to one-time.
    // VERIFIED bug on JT 2026-05-04: array form added the item but no
    // selling_plan_allocation appeared on the line; total = base price, no sub.
    //
    // Multi-item Phase 2 (welcome gifts when BX GWP ships): switch to INDEXED
    // array form (`items[0][id]=X&items[0][selling_plan]=Y&items[1][id]=Z`). The
    // explicit numeric index removes the parser ambiguity.

    var attribution = attributionParamsFromUrl();

    var addParts = [
      'id=' + encodeURIComponent(variantId),
      'quantity=1'
    ];
    if (planId) {
      addParts.push('selling_plan=' + encodeURIComponent(planId));
    }
    // Forward attribution as cart attributes so Triple Whale / Northbeam / Social
    // Snowball see them at /checkout. attributes[key]=value rides on the cart
    // through the redirect chain.
    for (var i = 0; i < attribution.length; i++) {
      var key = attribution[i][0];
      var val = attribution[i][1];
      addParts.push('attributes[' + encodeURIComponent(key) + ']=' + encodeURIComponent(val));
    }
    // Cohort attributes from <body data-experiment-id data-lp-variant> + window.JT_LP_CONTEXT.
    // Source of truth for experiment-cohort joins in BigQuery.
    var cohort = readLpCohort();
    if (cohort.experiment_id) {
      addParts.push('attributes[experiment_id]=' + encodeURIComponent(cohort.experiment_id));
    }
    if (cohort.lp_variant) {
      addParts.push('attributes[lp_variant]=' + encodeURIComponent(cohort.lp_variant));
    }
    if (cohort.lp_slug) {
      addParts.push('attributes[lp_slug]=' + encodeURIComponent(cohort.lp_slug));
    }
    if (cohort.lp_cluster) {
      addParts.push('attributes[lp_cluster]=' + encodeURIComponent(cohort.lp_cluster));
    }
    appendExtraAttrs(addParts, attrs);
    // Forward fbclid to /checkout URL too so theme.liquid's FBCLID capture
    // snippet (which only reads window.location.search, not cart attributes)
    // can still write the _fbc cookie at checkout if it was lost on the LP.
    // Defense-in-depth — primary path is the LP's own FBCLID capture writing
    // the cookie on landing. This is the fallback for users who somehow miss
    // the LP-side cookie (incognito, third-party-cookie blockers, etc.).
    var fbclidPair = null;
    for (var fi = 0; fi < attribution.length; fi++) {
      if (attribution[fi][0] === 'fbclid') { fbclidPair = attribution[fi]; break; }
    }
    // Build checkout path params. discount (optional) must come first — Shopify
    // only reads the first query param on /checkout reliably in the return_to chain.
    var checkoutParams = [];
    if (discount) checkoutParams.push('discount=' + encodeURIComponent(discount));
    if (fbclidPair) checkoutParams.push('fbclid=' + encodeURIComponent(fbclidPair[1]));
    var checkoutPath = '/checkout' + (checkoutParams.length ? '?' + checkoutParams.join('&') : '');
    addParts.push('return_to=' + checkoutPath);

    // Inner URL parts joined with `%26` (encoded `&`) so the OUTER return_to value isn't split.
    var cartAddPath = '/cart/add?' + addParts.join('%26');
    return SHOP + '/cart/clear?return_to=' + cartAddPath;
  }

  function fueledAddToCart(product) {
    function handleAddToCart() {
      window.fueled.productAdded({
        productId: product.productId,
        variant: product.variantId,
        variantId: product.variantId,
        variantName: product.variantName,
        sku: product.sku,
        brand: product.brand,
        category: product.category,
        name: product.productName,
        price: Number(product.price),
        url: product.productUrl,
        imageUrl: product.productImageUrl,
        currency: product.currency,
        quantity: product.quantity,
        timestamp: Date.now()
      });
    }

    if (window.fueled) {
      handleAddToCart();
    } else {
      window.addEventListener('fueled-ready', handleAddToCart);
    }
  }

  // Canonical add_to_cart event for client->server (CAPI) stitching. This is the
  // ONE place an add_to_cart fires — pages must NOT push their own (would double-fire).
  // value/price are read from an optional per-sku/tier `prices` map on
  // JT_CART_CONFIG (list price; checkout discounts are not reflected). Empty if absent.
  function pushDataLayerAddToCart(ids, tier) {
    if (!window.dataLayer) return;
    var inline = window.JT_CART_CONFIG || {};
    var skuData = (inline.skus && inline.skus[ids.sku]) || null;
    var itemName = (skuData && skuData.name) || inline.product_name || ids.sku;
    var price = '';
    try {
      if (inline.prices && inline.prices[ids.sku]) {
        price = inline.prices[ids.sku][tier] || '';
      }
    } catch (e) { /* prices map optional */ }
    // Extended product properties for Fueled/CAPI (requested 2026-06-02). Read
    // from the inline JT_CART_CONFIG.skus[sku] snapshot — same source as itemName.
    // Emitted with Fueled's exact key names (productId/variantName/category/
    // url/imageUrl). Absent on older pages that haven't been re-synced → undefined.
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: price,
        items: [{
          item_id: ids.sku || ids.variantId,
          item_name: itemName,
          item_brand: 'Just Thrive',
          variant_id: ids.variantId,
          selling_plan_id: ids.planId,
          price: price,
          quantity: 1,
          tier: tier,
          productId: skuData && skuData.product_id,
          variantName: skuData && skuData.variant_name,
          category: skuData && skuData.category,
          url: skuData && skuData.product_url,
          imageUrl: skuData && skuData.image_url,
          jt_lp_slug: inline.lp_slug,
          jt_lp_cluster: inline.lp_cluster
        }]
      }
    });

    fueledAddToCart({
      productId: skuData && skuData.product_id,
      variantId: ids.variantId,
      variantName: skuData && skuData.variant_name,
      sku: ids.sku,
      brand: 'Just Thrive',
      category: skuData && skuData.category,
      productName: itemName,
      price: price,
      productUrl: skuData && skuData.product_url,
      productImageUrl: skuData && skuData.image_url,
      currency: 'USD',
      quantity: 1
    });
  }

  function checkoutNow(sku, tier, attrs, discount) {
    return getRuntimeConfig().then(function (runtime) {
      var ids = resolveIds(runtime, sku, tier);
      pushDataLayerAddToCart(ids, tier);
      var url = buildCheckoutUrl(ids.variantId, ids.planId, attrs, discount);
      window.location.href = url;
    }).catch(function (err) {
      console.error('[JT cart] checkout failed:', err);
      try { window.alert('Sorry — there was an issue starting checkout. Please refresh the page and try again.'); }
      catch (e) { /* alert may be blocked */ }
    });
  }

  /**
   * Direct-to-checkout with explicit IDs. Used when the LP's tier picker has
   * data-variant and data-plan attributes per radio (cross-SKU tier picker —
   * e.g., 30-day uses JTPRO30 variant, 90-day uses JTPRO90 variant on the same
   * "Just Thrive Probiotic" parent product). Skips the SKU/tier registry lookup
   * entirely since the LP author already encoded the resolved IDs.
   */
  function checkoutWithIds(variantId, planId, meta) {
    if (!variantId) {
      console.error('[JT cart] checkoutWithIds called without variantId');
      return;
    }
    var ids = { variantId: variantId, planId: planId || null, sku: (meta && meta.sku) || (window.JT_CART_CONFIG && window.JT_CART_CONFIG.sku) || null };
    pushDataLayerAddToCart(ids, (meta && meta.tier) || (planId ? 'subscription' : 'onetime'));
    var url = buildCheckoutUrl(ids.variantId, ids.planId);
    window.location.href = url;
  }

  /**
   * Reads the selected radio in the tier picker. If the radio has data-variant
   * and data-plan attributes (current LP convention), use them directly. Falls
   * back to the legacy single-SKU + tier-string lookup for older LPs.
   */
  function tierPicked(sku) {
    var radio = document.querySelector('input[name="jt-tier"]:checked');
    if (radio && radio.dataset && radio.dataset.variant) {
      // New convention: explicit IDs on the radio. Cross-SKU compatible.
      return checkoutWithIds(
        radio.dataset.variant,
        radio.dataset.plan || null,
        { tier: radio.value, sku: radio.dataset.sku || sku }
      );
    }
    // Legacy fallback: SKU + tier string lookup against runtime config.
    var tier = radio ? radio.value : (window.JT_CART_CONFIG && window.JT_CART_CONFIG.default_tier) || '90day';
    return checkoutNow(sku || (window.JT_CART_CONFIG && window.JT_CART_CONFIG.sku), tier);
  }

  /**
   * Multi-item BUNDLE checkout — host-endorsement LP (probiotic subscription +
   * a bonus product made free by a discount). Used by the Candace June read:
   * JTPRO90 (90-day subscription) + JTBTR x3 (one-time Bitters), with a
   * SuperFiliate dynamic code zeroing the Bitters line.
   *
   * Final hop is /checkout (verified working with comma-stacked discount codes,
   * 2026-06-20). selling_plan binds to the subscription line only; the bonus Bitters
   * line stays one-time. The discount param carries the bonus (BXGY) code and, on a
   * 30-day mixed sub+gift cart, a stackable free-shipping code (see buildBundleUrl
   * for the mixed-cart shipping rationale — 90-day carts skip it).
   * opts = { sub:{sku,variantId,planId,tier}, bonus:{sku,variantId,qty},
   *          discount?, shippingDiscount?, attrs? }
   */
  function bundleDiscountCode() {
    try {
      var p = new URLSearchParams(window.location.search);
      return p.get('discount') || p.get('sf') ||
        (window.JT_CART_CONFIG && window.JT_CART_CONFIG.bundle_discount) || '';
    } catch (e) {
      return (window.JT_CART_CONFIG && window.JT_CART_CONFIG.bundle_discount) || '';
    }
  }

  function buildBundleUrl(opts) {
    // FIX 2026-06-02 (v2): matches the VERIFIED MannaVitality multi-line format.
    //   /cart/clear?return_to=/cart/add?items[][id]=SUB%26items[][quantity]=1
    //     %26items[][selling_plan]=PLAN%26items[][id]=BONUS%26items[][quantity]=N
    //     %26...%26return_to=/checkout?discount=CODE
    // Use the UNINDEXED items[][...] array with each LINE's keys grouped together
    // (id, then quantity, then selling_plan); the next items[][id] starts line 2.
    // selling_plan sits on line 0 (subscription) only — the bonus Bitters (line 1)
    // stay one-time, so OTP + SUB ride in one cart. The earlier indexed form
    // (items[0][...]) did NOT bind the plan on this store — the 90-day came through
    // as one-time. Inner `&` encoded as %26 so the outer return_to value isn't split.
    var addParts = [
      'items[][id]=' + encodeURIComponent(opts.sub.variantId),
      'items[][quantity]=1'
    ];
    if (opts.sub.planId) {
      addParts.push('items[][selling_plan]=' + encodeURIComponent(opts.sub.planId));
    }
    addParts.push('items[][id]=' + encodeURIComponent(opts.bonus.variantId));
    addParts.push('items[][quantity]=' + (opts.bonus.qty || 1));

    // OPTIONAL THIRD LINE (opts.addon) — the pre-purchase Prebiotic bump (Dylan LP,
    // 2026-07). Same grouped items[][...] form: id, quantity, then selling_plan if the
    // addon is itself a subscription (A/B paths carry addon.planId — e.g. 837943361
    // "Every 3 months"); a one-time addon (C path) omits planId. This makes TWO
    // selling_plan values ride the cart (probiotic sub + prebiotic sub); the grouped
    // unindexed form was manually verified to bind both (see dylan handoff). If a
    // checkout test ever shows the second plan dropping to one-time, fall back to the
    // indexed items[N][...] form (Risk #1 in JT_Dylan_Buybox_Upsell_Scope.md).
    if (opts.addon && opts.addon.variantId) {
      addParts.push('items[][id]=' + encodeURIComponent(opts.addon.variantId));
      addParts.push('items[][quantity]=' + (opts.addon.qty || 1));
      if (opts.addon.planId) {
        addParts.push('items[][selling_plan]=' + encodeURIComponent(opts.addon.planId));
      }
    }

    var attribution = attributionParamsFromUrl();
    for (var i = 0; i < attribution.length; i++) {
      addParts.push('attributes[' + encodeURIComponent(attribution[i][0]) + ']=' + encodeURIComponent(attribution[i][1]));
    }
    var cohort = readLpCohort();
    if (cohort.experiment_id) addParts.push('attributes[experiment_id]=' + encodeURIComponent(cohort.experiment_id));
    if (cohort.lp_variant)    addParts.push('attributes[lp_variant]='   + encodeURIComponent(cohort.lp_variant));
    if (cohort.lp_slug)       addParts.push('attributes[lp_slug]='      + encodeURIComponent(cohort.lp_slug));
    if (cohort.lp_cluster)    addParts.push('attributes[lp_cluster]='   + encodeURIComponent(cohort.lp_cluster));
    appendExtraAttrs(addParts, opts.attrs);

    // Final hop = /checkout. The single `discount` param may carry MULTIPLE codes,
    // comma-separated: the bonus (BXGY) code plus, on a 30-day mixed sub+gift cart,
    // a stackable free-shipping code. This is still ONE param — a second *param*
    // (e.g. &foo=) would not survive the double return_to decode, but a second
    // VALUE in the same param does. fbclid still reaches /checkout as a cart
    // attribute (attributes[fbclid]) via the loop above.
    //
    // WHY the free-shipping code (mixed-cart gotcha, verified at checkout 2026-06-20):
    // the sub + free-Bitters cart spans TWO Shopify delivery profiles. The
    // subscription line ships free via the "Subscription Shipping" profile (US = $0),
    // but the one-time Bitters line falls under the default "General" profile, whose
    // US rate is $7.99 for orders under $99. Shopify SUMS shipping PER PROFILE, so a
    // 30-day order ($44.99 sub, under $99) was charged $0 (sub) + $7.99 (bitters).
    // This is delivery-profile math, NOT a discount-combination bug. FREEBITTERS-SHIPPING
    // is a stackable free-shipping discount (US, min 2 items, kills rates <= $8.00)
    // that zeroes the Bitters rate. ONLY the 30-day tier passes it (opts.shippingDiscount,
    // sourced from OFFER_CONFIG.plan30.shipping_discount): 90-day carts ($119.99+) are
    // already over the $99 free-shipping threshold and ship free without it.
    //
    // MECHANICS (theme-specific, verified): comma-stacked codes only BOTH apply when
    // (a) passed in the ?discount= QUERY param and (b) the final hop is /checkout —
    // NOT on /cart, and NOT via the /discount/A,B PATH form. Each code is encoded
    // separately so the comma between them stays literal. PREREQUISITE: each 30-day
    // BXGY bonus code must have combinesWith.shippingDiscounts = true in Shopify, or
    // it blocks FREEBITTERS-SHIPPING.
    var codes = [];
    var primary = opts.discount || bundleDiscountCode();
    if (primary) codes.push(primary);
    if (opts.shippingDiscount) codes.push(opts.shippingDiscount);
    // Prebiotic-bump codes ride here (e.g. the addon's -$10 product code, and a
    // C-path free-shipping code). Each must have combinesWith.productDiscounts /
    // .shippingDiscounts = true in Shopify or it blocks the primary bonus code.
    // Same one-param, comma-separated, /checkout-final-hop rule as above.
    if (opts.extraDiscounts && opts.extraDiscounts.length) {
      for (var d = 0; d < opts.extraDiscounts.length; d++) {
        if (opts.extraDiscounts[d]) codes.push(opts.extraDiscounts[d]);
      }
    }
    var discountParam = codes.map(encodeURIComponent).join(',');
    var checkoutPath = '/checkout' + (discountParam ? '?discount=' + discountParam : '');
    addParts.push('return_to=' + checkoutPath);

    // Inner /cart/add parts joined with %26 so the OUTER return_to value isn't split.
    var cartAddPath = '/cart/add?' + addParts.join('%26');
    return SHOP + '/cart/clear?return_to=' + cartAddPath;
  }

  function checkoutBundle(opts) {
    opts = opts || {};
    if (!opts.sub || !opts.sub.variantId || !opts.bonus || !opts.bonus.variantId) {
      console.error('[JT cart] checkoutBundle needs sub.variantId and bonus.variantId');
      return;
    }
    try {
      pushDataLayerAddToCart(
        { sku: opts.sub.sku, variantId: opts.sub.variantId, planId: opts.sub.planId },
        opts.sub.tier || '90day'
      );
    } catch (e) { /* dataLayer optional */ }
    window.location.href = buildBundleUrl(opts);
  }

  /**
   * ONE-TIME (no subscription) checkout for N one-time line items + optional
   * discount code(s). Uses the SAME attribution-preserving /cart/clear → /cart/add
   * → /checkout chain as buildBundleUrl (NOT a bare /cart/{id}:qty permalink), so
   * utm_* / click ids ride to /checkout as cart attributes and the first-touch
   * cohort (experiment_id, lp_variant, lp_slug, lp_cluster) is tagged on the order.
   * This is what keeps tracking alive on the one-time tier — a bare permalink drops
   * every attribute (see the /cart/clear-wipes-attributes note on buildCheckoutUrl).
   *
   * opts = { items:[{variantId, qty}], discount?, extraDiscounts?:[code], attrs?, sku? }
   * All lines are one-time (no selling_plan). The discount rides the /checkout hop
   * (comma-stacked with extraDiscounts), same rule as the bundle path.
   */
  function buildOneTimeUrl(opts) {
    opts = opts || {};
    var items = opts.items || [];
    var addParts = [];
    for (var i = 0; i < items.length; i++) {
      addParts.push('items[][id]=' + encodeURIComponent(items[i].variantId));
      addParts.push('items[][quantity]=' + (items[i].qty || 1));
    }

    // Attribution + first-touch cohort — identical treatment to buildBundleUrl so
    // one-time and subscription orders tag the same way in BigQuery / Triple Whale.
    var attribution = attributionParamsFromUrl();
    for (var a = 0; a < attribution.length; a++) {
      addParts.push('attributes[' + encodeURIComponent(attribution[a][0]) + ']=' + encodeURIComponent(attribution[a][1]));
    }
    var cohort = readLpCohort();
    if (cohort.experiment_id) addParts.push('attributes[experiment_id]=' + encodeURIComponent(cohort.experiment_id));
    if (cohort.lp_variant)    addParts.push('attributes[lp_variant]='   + encodeURIComponent(cohort.lp_variant));
    if (cohort.lp_slug)       addParts.push('attributes[lp_slug]='      + encodeURIComponent(cohort.lp_slug));
    if (cohort.lp_cluster)    addParts.push('attributes[lp_cluster]='   + encodeURIComponent(cohort.lp_cluster));
    appendExtraAttrs(addParts, opts.attrs);

    // Discount(s) on the final /checkout hop (comma-stacked, one param) — same
    // mechanics as buildBundleUrl. Empty when no code (e.g. no ?otp -> full price).
    var codes = [];
    if (opts.discount) codes.push(opts.discount);
    if (opts.extraDiscounts && opts.extraDiscounts.length) {
      for (var d = 0; d < opts.extraDiscounts.length; d++) {
        if (opts.extraDiscounts[d]) codes.push(opts.extraDiscounts[d]);
      }
    }
    var discountParam = codes.map(encodeURIComponent).join(',');
    var checkoutPath = '/checkout' + (discountParam ? '?discount=' + discountParam : '');
    addParts.push('return_to=' + checkoutPath);

    var cartAddPath = '/cart/add?' + addParts.join('%26');
    return SHOP + '/cart/clear?return_to=' + cartAddPath;
  }

  function checkoutOneTime(opts) {
    opts = opts || {};
    var items = opts.items || [];
    if (!items.length || !items[0] || !items[0].variantId) {
      console.error('[JT cart] checkoutOneTime needs at least one item with variantId');
      return;
    }
    try {
      pushDataLayerAddToCart({ sku: opts.sku || null, variantId: items[0].variantId, planId: null }, 'onetime');
    } catch (e) { /* dataLayer optional */ }
    window.location.href = buildOneTimeUrl(opts);
  }

  window.JT = window.JT || {};
  window.JT.cart = {
    checkoutNow: checkoutNow,
    checkoutWithIds: checkoutWithIds,
    checkoutBundle: checkoutBundle,
    checkoutOneTime: checkoutOneTime,
    tierPicked: tierPicked,
    // Surfaced for /lp-verify and debugging:
    _resolveIds: resolveIds,
    _buildCheckoutUrl: buildCheckoutUrl,
    _buildBundleUrl: buildBundleUrl,
    _buildOneTimeUrl: buildOneTimeUrl,
    _getRuntimeConfig: getRuntimeConfig
  };

  if (window.console && console.log) {
    console.log('[JT cart] cart.js loaded. window.JT.cart.{checkoutNow, tierPicked} available.');
  }
})(window, document);
